'use client';

import React, { useState } from 'react';
import { ChevronDown, ChevronUp, RotateCcw } from 'lucide-react';
import type { SearchFilters } from './PropertySearchClient';
import LocationPicker from '@/components/LocationPicker';

interface FilterSidebarProps {
  filters: SearchFilters;
  updateFilter: <K extends keyof SearchFilters>(key: K, value: SearchFilters[K]) => void;
  resetFilters: () => void;
}

const PROPERTY_TYPES = ['Daire', 'Villa', 'Müstakil Ev', 'Rezidans', 'Dubleks', 'Penthouse', 'Yazlık', 'Arsa', 'İşyeri', 'Ofis'];
const BEDROOM_OPTIONS = ['1', '2', '3', '4', '5'];
const BATHROOM_OPTIONS = ['1', '2', '3', '4'];
const BUILDING_AGE_OPTIONS = [
  { value: '0-5', label: '0-5 Yıl (Sıfır)' },
  { value: '6-10', label: '6-10 Yıl' },
  { value: '11-20', label: '11-20 Yıl' },
  { value: '21+', label: '21+ Yıl (Eski)' },
];
const HEATING_OPTIONS = ['Doğalgaz', 'Kombi', 'Merkezi', 'Klima', 'Yerden Isıtma', 'Soba'];
const FEATURE_OPTIONS = [
  { value: 'otopark', label: 'Otopark' },
  { value: 'asansor', label: 'Asansör' },
  { value: 'balkon', label: 'Balkon' },
  { value: 'site', label: 'Site İçerisinde' },
  { value: 'guvenlik', label: 'Güvenlik' },
  { value: 'havuz', label: 'Havuz' },
  { value: 'deniz-manzarasi', label: 'Deniz Manzarası' },
  { value: 'esyali', label: 'Eşyalı' },
  { value: 'kredi-uygun', label: 'Krediye Uygun' },
  { value: 'iskan', label: 'İskanlı' },
  { value: 'bahce', label: 'Bahçe' },
  { value: 'spor-salonu', label: 'Spor Salonu' },
];

interface FilterSectionProps {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

function FilterSection({ title, children, defaultOpen = true }: FilterSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-border">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-3.5 text-sm font-bold text-foreground hover:bg-muted transition-colors"
      >
        {title}
        {isOpen ? <ChevronUp size={16} className="text-muted-foreground" /> : <ChevronDown size={16} className="text-muted-foreground" />}
      </button>
      {isOpen && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}

export default function FilterSidebar({ filters, updateFilter, resetFilters }: FilterSidebarProps) {
  const toggleArrayFilter = <K extends keyof SearchFilters>(key: K, value: string) => {
    const current = filters[key] as string[];
    const updated = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value];
    updateFilter(key, updated as SearchFilters[K]);
  };

  const activeCount = [
    filters.city ? 1 : 0,
    filters.district ? 1 : 0,
    filters.propertyType.length,
    (filters.priceMin || filters.priceMax) ? 1 : 0,
    (filters.areaMin || filters.areaMax) ? 1 : 0,
    filters.bedrooms.length,
    filters.bathrooms.length,
    filters.buildingAge ? 1 : 0,
    filters.heating.length,
    filters.features.length,
  ].reduce((a, b) => a + b, 0);

  return (
    <div className="text-sm">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border sticky top-0 bg-white z-10">
        <span className="font-bold text-foreground">
          Filtreler
          {activeCount > 0 && (
            <span className="ml-2 text-xs font-bold text-white bg-primary px-1.5 py-0.5 rounded-full">{activeCount}</span>
          )}
        </span>
        {activeCount > 0 && (
          <button
            onClick={resetFilters}
            className="flex items-center gap-1 text-xs font-semibold text-danger hover:text-danger/80 transition-colors"
          >
            <RotateCcw size={12} />
            Temizle
          </button>
        )}
      </div>

      {/* Konum — real DB hierarchy */}
      <FilterSection title="Konum">
        <LocationPicker
          selectedProvince={filters.city}
          selectedDistrict={filters.district}
          onProvinceChange={(val) => { updateFilter('city', val); updateFilter('district', ''); }}
          onDistrictChange={(val) => updateFilter('district', val)}
          compact
          className="flex-col"
        />
      </FilterSection>

      {/* İlan Türü */}
      <FilterSection title="İlan Türü">
        <div className="flex gap-2 flex-wrap">
          {[
            { value: 'satilik', label: 'Satılık' },
            { value: 'kiralik', label: 'Kiralık' },
          ].map((opt) => (
            <button
              key={`lt-filter-${opt.value}`}
              onClick={() => updateFilter('listingType', opt.value as SearchFilters['listingType'])}
              className={`flex-1 py-2 rounded-xl text-sm font-semibold border transition-all duration-150 ${filters.listingType === opt.value ? 'bg-primary text-white border-primary' : 'border-border text-foreground hover:border-primary hover:text-primary'}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </FilterSection>

      {/* Mülk Türü */}
      <FilterSection title="Mülk Türü">
        <div className="grid grid-cols-2 gap-1.5">
          {PROPERTY_TYPES.map((type) => (
            <button
              key={`pt-${type}`}
              onClick={() => toggleArrayFilter('propertyType', type)}
              className={`py-2 px-3 rounded-xl text-xs font-semibold border transition-all duration-150 text-left ${filters.propertyType.includes(type) ? 'bg-primary-light text-primary border-primary' : 'border-border text-foreground hover:border-primary/40'}`}
            >
              {type}
            </button>
          ))}
        </div>
      </FilterSection>

      {/* Fiyat Aralığı */}
      <FilterSection title="Fiyat Aralığı (₺)">
        <div className="flex gap-2 items-center">
          <div className="flex-1">
            <label className="text-xs text-muted-foreground font-medium mb-1 block">Min</label>
            <input
              type="text"
              value={filters.priceMin}
              onChange={(e) => updateFilter('priceMin', e.target.value)}
              placeholder="0"
              className="input-field text-sm py-2"
            />
          </div>
          <span className="text-muted-foreground mt-5">–</span>
          <div className="flex-1">
            <label className="text-xs text-muted-foreground font-medium mb-1 block">Max</label>
            <input
              type="text"
              value={filters.priceMax}
              onChange={(e) => updateFilter('priceMax', e.target.value)}
              placeholder="∞"
              className="input-field text-sm py-2"
            />
          </div>
        </div>
        <div className="mt-2 grid grid-cols-2 gap-1.5">
          {[
            { label: '5M altı', min: '', max: '5000000' },
            { label: '5M–10M', min: '5000000', max: '10000000' },
            { label: '10M–20M', min: '10000000', max: '20000000' },
            { label: '20M+', min: '20000000', max: '' },
          ].map((preset) => (
            <button
              key={`price-preset-${preset.label}`}
              onClick={() => { updateFilter('priceMin', preset.min); updateFilter('priceMax', preset.max); }}
              className={`py-1.5 px-2 rounded-lg text-xs font-semibold border transition-all ${filters.priceMin === preset.min && filters.priceMax === preset.max ? 'bg-primary-light text-primary border-primary' : 'border-border text-muted-foreground hover:border-primary/40 hover:text-foreground'}`}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </FilterSection>

      {/* Oda Sayısı */}
      <FilterSection title="Oda Sayısı">
        <div className="flex flex-wrap gap-2">
          {BEDROOM_OPTIONS.map((b) => (
            <button
              key={`bed-${b}`}
              onClick={() => toggleArrayFilter('bedrooms', b)}
              className={`w-12 h-10 rounded-xl text-sm font-bold border transition-all duration-150 ${filters.bedrooms.includes(b) ? 'bg-primary text-white border-primary' : 'border-border text-foreground hover:border-primary hover:text-primary'}`}
            >
              {b}+1
            </button>
          ))}
        </div>
      </FilterSection>

      {/* Banyo Sayısı */}
      <FilterSection title="Banyo Sayısı" defaultOpen={false}>
        <div className="flex gap-2">
          {BATHROOM_OPTIONS.map((b) => (
            <button
              key={`bath-${b}`}
              onClick={() => toggleArrayFilter('bathrooms', b)}
              className={`flex-1 h-10 rounded-xl text-sm font-bold border transition-all duration-150 ${filters.bathrooms.includes(b) ? 'bg-primary text-white border-primary' : 'border-border text-foreground hover:border-primary hover:text-primary'}`}
            >
              {b}
            </button>
          ))}
        </div>
      </FilterSection>

      {/* Metrekare */}
      <FilterSection title="Metrekare (m²)" defaultOpen={false}>
        <div className="flex gap-2 items-center">
          <div className="flex-1">
            <label className="text-xs text-muted-foreground font-medium mb-1 block">Min m²</label>
            <input
              type="text"
              value={filters.areaMin}
              onChange={(e) => updateFilter('areaMin', e.target.value)}
              placeholder="0"
              className="input-field text-sm py-2"
            />
          </div>
          <span className="text-muted-foreground mt-5">–</span>
          <div className="flex-1">
            <label className="text-xs text-muted-foreground font-medium mb-1 block">Max m²</label>
            <input
              type="text"
              value={filters.areaMax}
              onChange={(e) => updateFilter('areaMax', e.target.value)}
              placeholder="∞"
              className="input-field text-sm py-2"
            />
          </div>
        </div>
      </FilterSection>

      {/* Bina Yaşı */}
      <FilterSection title="Bina Yaşı" defaultOpen={false}>
        <div className="space-y-1.5">
          {BUILDING_AGE_OPTIONS.map((opt) => (
            <button
              key={`age-${opt.value}`}
              onClick={() => updateFilter('buildingAge', filters.buildingAge === opt.value ? '' : opt.value)}
              className={`w-full text-left py-2.5 px-3 rounded-xl text-sm font-medium border transition-all duration-150 ${filters.buildingAge === opt.value ? 'bg-primary-light text-primary border-primary font-semibold' : 'border-border text-foreground hover:border-primary/40'}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </FilterSection>

      {/* Isıtma */}
      <FilterSection title="Isıtma Sistemi" defaultOpen={false}>
        <div className="grid grid-cols-2 gap-1.5">
          {HEATING_OPTIONS.map((h) => (
            <button
              key={`heat-${h}`}
              onClick={() => toggleArrayFilter('heating', h)}
              className={`py-2 px-3 rounded-xl text-xs font-semibold border transition-all duration-150 text-left ${filters.heating.includes(h) ? 'bg-primary-light text-primary border-primary' : 'border-border text-foreground hover:border-primary/40'}`}
            >
              {h}
            </button>
          ))}
        </div>
      </FilterSection>

      {/* Özellikler */}
      <FilterSection title="Özellikler" defaultOpen={true}>
        <div className="grid grid-cols-1 gap-1.5">
          {FEATURE_OPTIONS.map((feat) => (
            <label
              key={`feat-${feat.value}`}
              className={`flex items-center gap-3 py-2.5 px-3 rounded-xl border cursor-pointer transition-all duration-150 ${filters.features.includes(feat.value) ? 'bg-primary-light border-primary' : 'border-border hover:border-primary/40'}`}
            >
              <div className={`w-4 h-4 rounded flex items-center justify-center border-2 flex-shrink-0 transition-all ${filters.features.includes(feat.value) ? 'bg-primary border-primary' : 'border-border'}`}>
                {filters.features.includes(feat.value) && (
                  <svg viewBox="0 0 10 8" fill="none" className="w-2.5 h-2.5">
                    <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>
              <input
                type="checkbox"
                className="sr-only"
                checked={filters.features.includes(feat.value)}
                onChange={() => toggleArrayFilter('features', feat.value)}
              />
              <span className={`text-sm font-medium ${filters.features.includes(feat.value) ? 'text-primary' : 'text-foreground'}`}>
                {feat.label}
              </span>
            </label>
          ))}
        </div>
      </FilterSection>

      {/* Tapu Durumu */}
      <FilterSection title="Tapu Durumu" defaultOpen={false}>
        <div className="space-y-1.5">
          {['Kat Mülkiyeti', 'Kat İrtifakı', 'Hisseli Tapu', 'Arsa Payı'].map((t) => (
            <button
              key={`tapu-${t}`}
              className="w-full text-left py-2.5 px-3 rounded-xl text-sm font-medium border border-border text-foreground hover:border-primary/40 hover:bg-primary-light transition-all"
            >
              {t}
            </button>
          ))}
        </div>
      </FilterSection>

      {/* Apply Button */}
      <div className="p-4">
        <button className="btn-primary w-full justify-center py-3">
          Filtreleri Uygula
        </button>
      </div>
    </div>
  );
}